import { Injectable } from '@angular/core';
import { Log } from '../models/log.model';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { GlobalService } from './global.service';

export interface UploadResponse {
  status: boolean;
  originalName: string;
  generatedName: string;
  msg: string;
  imageUrl: string;
  thumbnailUrl: string;
}

export interface MediaMultipartStartResponse {
  uploadId: string;
  objectKey: string;
  partSize: number;
}

export interface MediaMultipartPart {
  partNumber: number;
  eTag: string;
}

@Injectable({ providedIn: 'root' })
export class fileUploadService {
  apiHost: string = environment.apiHost;

  constructor(private http: HttpClient, private globalService: GlobalService) { }

  uploadFile(file: any, fileName: string, type: string): Promise<string> {
    let input = new FormData();
    input.append('file', file, fileName);
    input.append('Content-Type', file.type);
    let promise = new Promise<string>((resolve, reject) => {
      return this.http
        .post<string>(this.apiHost + '/file/fileUpload?Type=' + type, input)
        .toPromise()
        .then(
          (data) => {
            if (data != null)
              resolve(data['imageUrl']);
            else
              resolve('');
          },
          (msg) => {
            reject('Internal Error: cannot upload file!');
          }
        );
    });
    return promise;
  }

  uploadMediaFile(
    file: any,
    fileName: string,
    type: string
  ): Promise<UploadResponse> {

    let input = new FormData();

    input.append('file', file, fileName);
    input.append('Content-Type', file.type);

    return new Promise<UploadResponse>((resolve, reject) => {

      this.http
        .post<UploadResponse>(
          this.apiHost + '/file/fileUpload?Type=' + type,
          input
        )
        .toPromise()
        .then(
          (data) => {
            if (data != null)
              resolve(data);
            else
              resolve(null);
          },
          (msg) => {
            reject('Internal Error: cannot upload media file!');
          }
        );
    });
  }

  LogoUpload(file: any, subdomain: string): Promise<string> {
    let input = new FormData();
    input.append('file', file, "icon-512x512.png");
    input.append('Content-Type', file.type);
    let promise = new Promise<string>((resolve, reject) => {
      return this.http
        .post<string>(this.apiHost + '/file/LogoUpload?Subdomain=' + subdomain, input)
        .toPromise()
        .then(
          (data) => {
            if (data == null)
              resolve('');
            else
              resolve(data['imageUrl']);
          },
          (msg) => {
            reject('Internal Error: cannot Logo Upload!');
          }
        );
    });
    return promise;
  }

  ResetLogo(subdomain: string): Promise<string> {
    let promise = new Promise<string>((resolve, reject) => {
      return this.http
        .post<string>(this.apiHost + '/file/ResetLogo?Subdomain=' + subdomain, null)
        .toPromise()
        .then(
          (data) => {
            resolve(data['imageUrl']);
          },
          (msg) => {
            reject('Internal Error: cannot Reset Logo!');
          }
        );
    });
    return promise;
  }

  importFile(file: any): Promise<string> {
    let input = new FormData();
    input.append('file', file, file.name);
    input.append('Content-Type', file.type);
    let promise = new Promise<string>((resolve, reject) => {
      return this.http
        .post<string>(this.apiHost + '/file/importFile', input)
        .toPromise()
        .then(
          (data) => {
            resolve(data['message']);
          },
          (msg) => {
            reject('');
          }
        );
    });
    return promise;
  }

  clearImport(): Promise<any> {
    let promise = new Promise<any>((resolve, reject) => {
      return this.http
        .post<any>(this.apiHost + '/file/clearImport', null)
        .toPromise()
        .then(
          (data) => {
            resolve('');
          },
          (msg) => {
            reject('');
          }
        );
    });
    return promise;
  }

  deleteFile(folder: any, fileName: string): Promise<void> {
    let promise = new Promise<void>((resolve, reject) => {
      return this.http
        .post<void>(
          this.apiHost +
          '/file/DeleteFile?Folder=' +
          folder +
          '&File=' +
          fileName,
          null
        )
        .toPromise()
        .then(
          () => {
            resolve();
          },
          (msg) => {
            reject('Internal Error: cannot delete file!');
          }
        );
    });
    return promise;
  }

  async startMediaMultipartUpload(
    file: File,
    type: string
  ): Promise<MediaMultipartStartResponse> {

    const response = await this.http.post<MediaMultipartStartResponse>(
      this.apiHost + '/file/MediaMultipartStart',
      {
        fileName: file.name,
        contentType: file.type,
        type: type
      }
    ).toPromise();

    if (!response) {
      throw new Error('Unable to start media upload.');
    }

    return response;
  }

  async getMediaMultipartPartUrl(
    uploadId: string,
    objectKey: string,
    partNumber: number
  ): Promise<string> {

    const response = await this.http.get<{ url: string }>(
      this.apiHost + '/file/MediaMultipartPartUrl',
      {
        params: {
          UploadId: uploadId,
          ObjectKey: objectKey,
          PartNumber: partNumber.toString()
        }
      }
    ).toPromise();

    if (!response || !response.url) {
      throw new Error('Unable to get upload URL.');
    }

    return response.url;
  }

  async completeMediaMultipartUpload(
    uploadId: string,
    objectKey: string,
    parts: MediaMultipartPart[]
  ): Promise<UploadResponse> {

    const response = await this.http.post<UploadResponse>(
      this.apiHost + '/file/MediaMultipartComplete',
      {
        uploadId: uploadId,
        objectKey: objectKey,
        parts: parts
      }
    ).toPromise();

    if (!response) {
      throw new Error('Unable to complete media upload.');
    }

    return response;
  }

  async cancelMediaMultipartUpload(
    uploadId: string,
    objectKey: string
  ): Promise<void> {

    await this.http.post(
      this.apiHost + '/file/MediaMultipartCancel',
      {
        uploadId: uploadId,
        objectKey: objectKey,
        parts: []
      }
    ).toPromise();
  }

async uploadMediaMultipart(
  file: File,
  type: string,
  onProgress?: (percent: number) => void
): Promise<UploadResponse> {

  // 1. Start multipart upload
  const start = await this.startMediaMultipartUpload(file, type);

  const partSize = start.partSize;
  const totalParts = Math.ceil(file.size / partSize);

  const uploadedParts: MediaMultipartPart[] = [];

  let uploadedBytes = 0;

  try {

    // 2. Upload each part
    for (
      let partNumber = 1;
      partNumber <= totalParts;
      partNumber++
    ) {

      const startByte = (partNumber - 1) * partSize;

      const endByte = Math.min(
        startByte + partSize,
        file.size
      );

      // Get the chunk from the original file
      const blob = file.slice(
        startByte,
        endByte
      );

      // Diagnostic logging
      console.log('Media upload part:', {
        partNumber: partNumber,
        totalParts: totalParts,
        startByte: startByte,
        endByte: endByte,
        fileSize: file.size,
        blobSize: blob.size,
        blobType: blob.type
      });

      // IMPORTANT:
      // Convert Blob to ArrayBuffer before sending.
      // This helps avoid iOS Safari sending a zero-byte
      // request body when using xhr.send(blob).
      const arrayBuffer = await blob.arrayBuffer();

      console.log('Media upload ArrayBuffer:', {
        partNumber: partNumber,
        byteLength: arrayBuffer.byteLength
      });

      // Make sure the chunk isn't empty
      if (arrayBuffer.byteLength === 0) {
        throw new Error(
          `Upload part ${partNumber} is empty. ` +
          `Blob size: ${blob.size}, ` +
          `File size: ${file.size}`
        );
      }

      // Get presigned URL
      const url = await this.getMediaMultipartPartUrl(
        start.uploadId,
        start.objectKey,
        partNumber
      );

      // Upload directly to S3
      const etag = await new Promise<string>(
        (resolve, reject) => {

          const xhr = new XMLHttpRequest();

          xhr.open(
            'PUT',
            url,
            true
          );

          xhr.setRequestHeader(
            'Content-Type',
            file.type || 'application/octet-stream'
          );

          xhr.upload.onprogress = (event) => {

            if (event.lengthComputable) {

              const currentPartBytes =
                event.loaded;

              const totalUploaded =
                uploadedBytes +
                currentPartBytes;

              const percent =
                Math.round(
                  (totalUploaded / file.size) * 100
                );

              if (onProgress) {
                onProgress(percent);
              }
            }
          };

          xhr.onload = () => {

            if (
              xhr.status >= 200 &&
              xhr.status < 300
            ) {

              const etag =
                xhr.getResponseHeader('ETag');

              if (!etag) {

                reject(
                  new Error(
                    'S3 did not return an ETag.'
                  )
                );

                return;
              }

              resolve(etag);
            }
            else {

              reject(
                new Error(
                  `S3 upload failed (${xhr.status}).`
                )
              );
            }
          };

          xhr.onerror = () => {

            reject(
              new Error(
                'S3 upload failed.'
              )
            );
          };

          xhr.onabort = () => {

            reject(
              new Error(
                'S3 upload was aborted.'
              )
            );
          };

          // IMPORTANT:
          // Send the ArrayBuffer instead of the Blob.
          xhr.send(arrayBuffer);
        }
      );

      // Store the successfully uploaded part
      uploadedParts.push({
        partNumber: partNumber,
        eTag: etag
      });

      // Update total uploaded bytes
      uploadedBytes += arrayBuffer.byteLength;

      if (onProgress) {

        onProgress(
          Math.round(
            (uploadedBytes / file.size) * 100
          )
        );
      }
    }

    // 3. Complete multipart upload
    return await this.completeMediaMultipartUpload(
      start.uploadId,
      start.objectKey,
      uploadedParts
    );

  }
  catch (error) {

    console.error(
      'Media multipart upload failed:',
      error
    );

    // 4. Clean up incomplete S3 multipart upload
    try {

      await this.cancelMediaMultipartUpload(
        start.uploadId,
        start.objectKey
      );

    }
    catch (cancelError) {

      console.error(
        'Unable to cancel multipart upload:',
        cancelError
      );
    }

    throw error;
  }
}

uploadMediaFilesBatch(
  files: File[],
  uploadType: string
): Promise<UploadResponse[]> {

  const formData = new FormData();

  files.forEach(file => {
    formData.append('files', file, file.name);
  });

  return this.http.post<UploadResponse[]>(
    this.apiHost + '/file/FileUploadBatch?Type=' + uploadType,
    formData
  ).toPromise().then(data => data || []);
}
}
